import * as d3 from 'd3';
import {IAnalyticsChartsTooltipHandler} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-tooltip-handler.interface";
import {
  AnalyticsChartSortOrder,
  IAnalyticsChartsConfigItem
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-config.interface";

/**
 * a base class for building charts
 */
export abstract class AnalyticsChartsVisualizer {

  protected singleColor: string = "#2b86b2";

  protected svgHeight: number = 275;
  protected svgWidth: number;
  protected svgClientHeight;
  protected svgClientWidth;

  protected divContainer: any;
  protected svg: any;
  protected chartGraphic: any;
  protected graphicMargins: any;

  protected tooltipHandler: IAnalyticsChartsTooltipHandler;
  protected chartInternalConfig: any;
  protected chartInternalData: any;
  protected chartDataEntries: any;
  //------------------------------------------------------------------------------------

  public withTooltipHandler(tooltipHandler: IAnalyticsChartsTooltipHandler): AnalyticsChartsVisualizer {
    this.tooltipHandler = tooltipHandler;
    return this;
  }

  public withConfig(chartInternalConfig: any): AnalyticsChartsVisualizer {
    this.chartInternalConfig = chartInternalConfig;
    return this;
  }

  public withData(chartInternalData: any): AnalyticsChartsVisualizer {
    this.chartInternalData = chartInternalData;
    return this;
  }
  //------------------------------------------------------------------------------------

  buildChart(): void {

    const self = this;

    // remove first old element
    d3.select("#"+self.chartInternalData.svgId).remove();

    // draw a chart under the DIV container
    self.divContainer = d3.select(self.chartInternalConfig.d3DivId);

    // set the dimensions and margins of the graph
    self.graphicMargins = self.getGraphicDimensions();
    self.svgWidth = self.graphicMargins.width;
    self.svgClientHeight = self.svgHeight - self.graphicMargins.top - self.graphicMargins.bottom;
    self.svgClientWidth = self.svgWidth - self.graphicMargins.left - self.graphicMargins.right;

    // append the svg object to the div element:
    self.svg = self.divContainer
      .append("svg")
      .attr("id", self.chartInternalData.svgId)
      .attr("class", "an-charts")
      .attr("width", "100%")
      .attr("height", self.svgHeight);

    let graphicX = (self.graphicMargins.left + 10);
    self.chartGraphic = self.svg.append("g")
      .attr("transform", "translate(" + graphicX + "," + self.graphicMargins.top + ")");

    // draw the chart's header
    let labelX = 15;
    let labelY = 30;

    let gLabel = self.svg.append("text")
      .text(self.chartInternalData.header)
      .attr("x", labelX)
      .attr("y", labelY)
      .attr("class", "svg-header-insights");

    // no chart to display when there is no data
    if (self.chartInternalData.dataLength == 0) {
      self.svg.append("text")
        .text("No Data")
        .attr("x", labelX)
        .attr("y", labelY + 15)
        .attr("class", "svg-header-sub");

      return;
    }

    // set the integrated data of the specific chart. The output is an array with this format:
    //  {key1: AnalyticsChartCategory, key2: AnalyticsChartCategory ...}
    this.chartDataEntries = d3.entries(self.chartInternalData.data);

    // Note:
    //    The order of the returned 'entries' array is undefined so we need to ensure that the
    //    'entries' array is sorted similar to the provided data
    let sortedKeys = self.chartInternalData.sortedKeys;
    this.chartDataEntries.sort((d1, d2) => {
      let index1 = sortedKeys.indexOf(d1.key);
      let index2 = sortedKeys.indexOf(d2.key);

      if (index1 > index2) return 1;
      if (index1 < index2) return -1;
      return 0;
    });

    // invoke child to build the specific chart element
    let d3ChartElement = self.createChartElement(self);

    // attach mouse events for the tooltip
    self.attachTooltipEvents(d3ChartElement, self);

    // d3ChartElement.on("mouseover", function(d, i) {
    //   let label='<span>'+self.getLegendLabel(self.chartDataEntries[i].value)+'</span>';
    //   self.tooltipHandler.onMouseOver(d3.event, this, label);
    //
    //   self.changeStrokeOnMouseEvent(true, this);
    // })
    // .on("mouseout", function(d, i) {
    //   self.tooltipHandler.onMouseOut();
    //
    //   self.changeStrokeOnMouseEvent(false, this);
    // });

    // create the legend (only if supported by specific chart)
    if (self.hasLegend()) {

      const legendScaleX = 20;
      let legendG = self.svg.append("g")
        .attr("transform", "translate(" + legendScaleX + "," + 40 + ")");

      const legendItemScaleX = self.graphicMargins.left + legendScaleX;
      let legendItemsG = legendG.selectAll(".legend")
        .data(self.chartDataEntries) // Note: the legend's data is set with the specific chart's chartDataEntries
        .enter().append("g")
        .attr("transform", function (d, i) {
          return "translate(" + legendItemScaleX + "," + (i * 23 + 20) + ")"; // place each legend on the right and bump each one down 23 pixels
        });

      // get the X end-position of the SVG
      let legendStartX = 71; // self.svgClientWidth - graphicX + 10;

      let legendRectDim = 12;
      legendItemsG.append("rect") // make a matching color rect
        .attr("width", legendRectDim)
        .attr("height", legendRectDim)
        .attr("x", legendStartX)
        .attr("fill", function (d, i) {
          return self.chartInternalData.colors[i];
        });

      // add the legend's text
      let legendLabelElement = legendItemsG.append("text")
        .text(function (d) {
          return self.getLegendLabel(d.value);
        })
        .attr("class", "legend-label")
        .attr("y", 10)
        .attr("x", legendStartX + legendRectDim + 5);

      // attach mouse events for the tooltip
      self.attachTooltipEvents(legendLabelElement, self);

    }
  }
  //------------------------------------------------------------------------------------

  abstract createChartElement(self: any): any;
  abstract getGraphicDimensions(): any;
  //------------------------------------------------------------------------------------

  hasLegend(): boolean {
    return true;
  }
  //------------------------------------------------------------------------------------

  getLegendLabel(category) {

    let metricLabel = category.metricLabel;
    if (! metricLabel || metricLabel.length == 0) {
      metricLabel = category.percentage + "% (" + category.count + ')';
    }

    return category.label + " | " + metricLabel;
  }
  //------------------------------------------------------------------------------------

  /**
   * return the unit by which category value is being referred
   */
  getCategoryValue(category) {
    return category.percentage;
  }
  //------------------------------------------------------------------------------------

  changeStrokeOnMouseEvent(mouseOn: boolean, srcElement: any) {

    // Note: in order to have the effect of a raised and bold element when it is in focus,
    //       the element's opacity is set by default to 0 (element looks as sunk) and
    //       when the element got the focus, opacity is set to 1 (element looks as raised)
    let strokeOpacity: number = mouseOn ? 0 : 1;
    d3.select(srcElement).style('stroke-opacity', strokeOpacity);
  }
  //------------------------------------------------------------------------------------

  getDefaultSortOrder(): AnalyticsChartSortOrder {
    // /* by default, values are sorted DESC - from bigger to smaller */
    // force ASC order so empty bars will be at far from the scale and bars with value will be near the scale
    return AnalyticsChartSortOrder.asc;
  }
  //------------------------------------------------------------------------------------

  getChartCustomData() {
    return this.chartInternalConfig.chartCustomData;
  }
  //------------------------------------------------------------------------------------

  getChartConfigItem(): IAnalyticsChartsConfigItem {
    return this.chartInternalConfig.data;
  }
  //------------------------------------------------------------------------------------

  attachTooltipEvents(element, self) {

    // attach mouse events for the tooltip
    element.on("mouseover", function(d, i) {
      let label='<span>'+self.getLegendLabel(self.chartDataEntries[i].value)+'</span>';
      self.tooltipHandler.onMouseOver(d3.event, this, label);

      self.changeStrokeOnMouseEvent(true, this);
    })
      .on("mouseout", function(d, i) {
        self.tooltipHandler.onMouseOut();

        self.changeStrokeOnMouseEvent(false, this);
      });

  }
  //------------------------------------------------------------------------------------

}
//=====================================================================================
//=====================================================================================

/**
 * specific class builder for Pie/Donut charts
 */
export class AnalyticsChartsPieVisualizer extends AnalyticsChartsVisualizer {

  constructor(private isPie: boolean) {
    super();
  }
  //------------------------------------------------------------------------------------

  createChartElement(self: any): any {

    // The outerRadius of the pie plot is the smallest of width / height, subtracted a bit of margin.
    let outerRadius = Math.min(self.graphicMargins.left, self.graphicMargins.top) - self.graphicMargins.offsetX;
    let innerRadius = (self.svgWidth / 4) - 10;
    if (self.isPie) innerRadius = 0;

    // build the colors pallet
    let colors = d3.scaleOrdinal()
      .domain(self.chartInternalData.data)
      .range(self.chartInternalData.colors);

    // Compute the position of each group on the pie:
    let pie = d3.pie()
      .value(function(d) {return self.getCategoryValue(d.value); })
      .sort(null);

    let chartDataReady = pie(self.chartDataEntries);

    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    let d3ChartElement = self.chartGraphic
      .selectAll('pieChart')
      .data(chartDataReady)
      .enter()
      .append('path')
      .attr('d', d3.arc()
        .innerRadius(innerRadius) // This is the size of the donut hole
        .outerRadius(outerRadius))
      .attr('fill', function(d){ return(colors(d.data.key)) })
      .attr('class', 'an-chart-plot');

    return d3ChartElement;
  }
  //------------------------------------------------------------------------------------

  getGraphicDimensions(): any {
    let width = 300;
    return {width:width, top: (this.svgHeight*0.55), right: 0, bottom: 0, left: (width / 2)-12, offsetX: (width*0.14)};
  }
  //------------------------------------------------------------------------------------

}
//=====================================================================================
//=====================================================================================

/**
 * specific class builder for Bar-Plots charts
 */
export class AnalyticsChartsBarVisualizer extends AnalyticsChartsVisualizer {

  protected maxBarSize: number = 16;

  createChartElement(self: any): any {

    // initialize the categories axis

    // build the key entries
    let keyEntries = [];
    self.chartDataEntries.forEach(function(d) {
      keyEntries.push(d.key);
    });

    let scaleCategories = d3.scaleBand()
      .domain(keyEntries)
      .range(self.getCategoriesScaleRange())
      .padding(0.1);

    // create the categories axis, where the ticks line is hidden. Use the tickValues as ticks
    let axisCategories = self.getCategoriesScaleFunc()(scaleCategories)
      .tickSize(0)  // labels on categories axis should not have the dash line
      .tickValues(keyEntries);

    // add the categories Axis
    let categoriesAxisElement = self.chartGraphic.append("g")
      .attr("class", "chart-bar-axis")
      .call(axisCategories);

    self.setCategoriesAxis(categoriesAxisElement);

    let axisLabelElement: any = categoriesAxisElement
      .selectAll("text")
      .attr("class", "chart-bar-axis-label");
    // .style("text-anchor", "start")

    self.setCategoriesAxisLabel(axisLabelElement);

    // Note: a tooltip already is attached to the bar element
    // attach mouse events for the tooltip
    // self.attachTooltipEvents(axisLabelElement, self);
    //----------------------------------------------------------------------------------

    // initialize the values axis

    // get the number of ticks in the values - if not configured, use default
    // Note: since that the values range is in percent, we present the values in groups of 20
    let scaleValuesSize = 5;
    if (! isNaN(this.getChartConfigItem().scaleValuesSize)) {
      scaleValuesSize = this.getChartConfigItem().scaleValuesSize;
    }

    // calculate the min/max scale values
    let scaleDomain = this.calcValuesDomain();

    let scaleValues = d3.scaleLinear()
      .domain(scaleDomain)
      .range(self.getValuesScaleRange())
      .nice();

    // create the values-axis
    let axisValues = self.getValuesScaleFunc()(scaleValues)
      .ticks(scaleValuesSize);

    // add the values Axis
    let valuesAxisElement = self.chartGraphic.append("g")
      .attr("class", "chart-bar-axis")
      .call(axisValues);

    self.setValuesAxis(valuesAxisElement);

    valuesAxisElement
      .selectAll("text")
      .attr("class", "chart-bar-axis-label");
    //----------------------------------------------------------------------------------

    // all bars should have the same color
    // // get the colors
    // let colors = d3.scaleOrdinal()
    //   .domain(self.chartDataEntries)
    //   .range(self.chartInternalData.colors);

    let d3ChartElement = self.chartGraphic.selectAll(".bar")
      .data(self.chartDataEntries)
      .enter()
      .append("rect")
      .attr('class', 'an-chart-plot-bar');
      // .attr("fill", function (d) {
      //   return self.singleColor; // all bars should have the same color
      // });

    let chartObject = self.setChartElement(self, d3ChartElement, scaleCategories, scaleValues);
    //----------------------------------------------------------------------------------

    // add labels to the category bars
    let categoriesLabelElement = self.chartGraphic.selectAll(".text")
      .data(self.chartDataEntries)
      .enter()
      .append("text")
      .attr("class","an-chart-bar-label")
      .attr("text-anchor", "start")
      .text(function(d) {
        return d.value.percentage + '%';
      });

    self.setCategoriesLabel(self, categoriesLabelElement, scaleCategories, scaleValues, chartObject);
    //----------------------------------------------------------------------------------

    return d3ChartElement;
  }
  //------------------------------------------------------------------------------------

  getGraphicDimensions(): any {

    let margins = {width:340, top: 50, right: 0, bottom: 30, left: 70, offsetX: 0};
    if (this.isHorizontal()) {
      margins.left = 130;
    } else {
      margins.bottom = 90;
    }
    return margins;
  }
  //------------------------------------------------------------------------------------

  isHorizontal(): boolean {
    return false;
  }
  //------------------------------------------------------------------------------------

  /**
   * calculate the min/max scale values
   */
  calcValuesDomain(): Array<number> {

    // get if range is defined in the configuration
    let configItem = this.getChartConfigItem();
    let chartCustomData = this.getChartCustomData();

    // if range is configured:
    if (!isNaN(configItem.scaleValuesRangeMin) && !isNaN(configItem.scaleValuesRangeMax)) {

      chartCustomData.scaleValuesRangeMin = configItem.scaleValuesRangeMin;
      chartCustomData.scaleValuesRangeMax = configItem.scaleValuesRangeMax;

    // range is not configured AND chartCustomData was not initialized:
    } else if (isNaN(chartCustomData.scaleValuesRangeMin)) {

      // set the min/max scale values (in percentage)
      chartCustomData.scaleValuesRangeMin = 0;
      chartCustomData.scaleValuesRangeMax = 100;

    }

    return [chartCustomData.scaleValuesRangeMin, chartCustomData.scaleValuesRangeMax];
  }
  //------------------------------------------------------------------------------------

  hasLegend(): boolean {
    return false; // legend is not relevant to bar chart
  }
  //------------------------------------------------------------------------------------

  getDefaultSortOrder(): AnalyticsChartSortOrder {
    // by default, Bar chart values are sorted ASC - from smaller to bigger
    return AnalyticsChartSortOrder.asc;
  }
  //------------------------------------------------------------------------------------

  getCategoriesScaleRange(): Array<any> {
    
    return [ 0, this.svgClientWidth ];  // scale's range is 0 to SVG width
  }
  //------------------------------------------------------------------------------------

  getCategoriesScaleFunc(): Function {
    return d3.axisBottom; // categories are on the bottom to the X axis
  }
  //------------------------------------------------------------------------------------

  getValuesScaleRange(): Array<any> {

    return [ this.svgClientHeight, 0 ]; // scale's range is SVG height to 0
  }
  //------------------------------------------------------------------------------------

  getValuesScaleFunc(): Function {
    return d3.axisLeft; // scale values are on the left to the Y axis
  }
  //------------------------------------------------------------------------------------

  setChartElement(self: any, d3ChartElement: any, scaleCategories: any, scaleValues: any): any {

    // add margins so the bar won't hide the axis
    let chartObj = {
      yMargin: 1,
      xMargin: 2
    };

    d3ChartElement
      .attr("x", function(d) { return scaleCategories(d.key)+chartObj.xMargin; })
      .attr("y", function(d) { return scaleValues(self.getCategoryValue(d.value))-chartObj.yMargin; })
      .attr("width", Math.min(scaleCategories.bandwidth(), self.maxBarSize))
      .attr("height", function(d) { return self.svgClientHeight - scaleValues(self.getCategoryValue(d.value)); });

    return chartObj;
  }
  //------------------------------------------------------------------------------------

  setCategoriesAxis(axisElement: any) {

    axisElement
      .attr("transform", "translate(0," + this.svgClientHeight + ")");

  }
  //------------------------------------------------------------------------------------

  setCategoriesAxisLabel(axisLabelElement: any) {

    axisLabelElement
      .attr("y", "5"); // set the position of the labels relative to the axis

  }
  //------------------------------------------------------------------------------------

  setCategoriesLabel(self: any, labelElement: any, scaleCategories: any, scaleValues: any, chartObj: any) {

    // add margins so the bar won't hide the axis
    let labelMargin = 4;

    labelElement
      .attr("dy", ".75em")  // set the label's position to the center of the bar
      .attr("x", (function(d) { return scaleCategories(d.key)+labelMargin; }  ))
      .attr("y", function(d) { return scaleValues(self.getCategoryValue(d.value)) - 13; })

  }
  //------------------------------------------------------------------------------------

  setValuesAxis(axisElement: any) {}
  //------------------------------------------------------------------------------------

}
//=====================================================================================
//=====================================================================================

/**
 * specific class builder for Horizontal Bar-Plots charts
 */
export class AnalyticsChartsHBarVisualizer extends AnalyticsChartsBarVisualizer {

  getCategoriesScaleRange(): Array<any> {
    
    return [ this.svgClientHeight, 0 ]; // scale's range is SVG height to 0
  }
  //------------------------------------------------------------------------------------

  getCategoriesScaleFunc(): Function {
    return d3.axisLeft; // categories are on the left to the Y axis
  }
  //------------------------------------------------------------------------------------

  getValuesScaleRange(): Array<any> {

    return [ 0, this.svgClientWidth ]; // scale's range is 0 to SVG width
  }
  //------------------------------------------------------------------------------------

  getValuesScaleFunc(): Function {
    return d3.axisBottom; // scale values are on the bottom to the Y axis
  }
  //------------------------------------------------------------------------------------

  setChartElement(self: any, d3ChartElement: any, scaleCategories: any, scaleValues: any): any {

    // add margins so the bar won't hide the axis
    let chartObj: any = {};
    chartObj.yMargin = 0;
    chartObj.xMargin = 2;
    chartObj.barDefHeight = scaleCategories.bandwidth();
    chartObj.barOffset = (chartObj.barDefHeight - self.maxBarSize)/2;
    chartObj.barHeight = Math.min(chartObj.barDefHeight, self.maxBarSize) +chartObj.yMargin;
    chartObj.funcY = function(d) { return scaleCategories(d.key)+chartObj.yMargin+chartObj.barOffset };

    d3ChartElement
      .attr("width", function(d) {return scaleValues(self.getCategoryValue(d.value)); } )
      .attr("height", chartObj.barHeight)
      .attr("y", chartObj.funcY)
      .attr("x", function(d) { return chartObj.xMargin; });

    return chartObj;
  }
  //------------------------------------------------------------------------------------

  setCategoriesAxis(axisElement: any) {}
  //------------------------------------------------------------------------------------

  setCategoriesAxisLabel(axisLabelElement: any) {

    axisLabelElement
      .attr("x", "-3") // set the position of the label relative to the Y axis
      .attr("y", "1")  // set the position of the label to the center of the bar
      .style("text-anchor", "end") // text is aligned to the end

  }
  //------------------------------------------------------------------------------------

  setCategoriesLabel(self: any, labelElement: any, scaleCategories: any, scaleValues: any, chartObj: any) {

    labelElement
      .attr("dy", "1.1em") // set the label's position to the center of the bar
      .attr("y", chartObj.funcY)
      .attr("x", function(d) { return scaleValues(self.getCategoryValue(d.value)) + 3.5; })

  }
  //------------------------------------------------------------------------------------

  setValuesAxis(axisElement: any) {

    axisElement
      .attr("transform", "translate(0," + this.svgClientHeight + ")");

  }
  //------------------------------------------------------------------------------------

  isHorizontal(): boolean {
    return true;
  }
  //------------------------------------------------------------------------------------

}
//=====================================================================================
//=====================================================================================
